import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  factoryService,
  type FactoryOrder,
} from '../services/factoryService';

const STORAGE_KEY = 'factory.activeOrderId';

type FactoryOrderScopeValue = {
  activeOrderId: string | null;
  activeOrder: FactoryOrder | null;
  setActiveOrderId: (id: string | null) => void;
  orders: FactoryOrder[];
  ordersLoading: boolean;
};

const FactoryOrderScopeContext = createContext<FactoryOrderScopeValue | null>(null);

function readStoredOrderId(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

function writeStoredOrderId(id: string | null) {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

export function FactoryOrderScopeProvider({
  children,
  refreshKey = 0,
}: PropsWithChildren<{ refreshKey?: number }>) {
  const [orders, setOrders] = useState<FactoryOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [activeOrderId, setActiveOrderIdState] = useState<string | null>(readStoredOrderId);

  const setActiveOrderId = useCallback((id: string | null) => {
    const next = id && id.trim() ? id.trim() : null;
    setActiveOrderIdState(next);
    writeStoredOrderId(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (refreshKey > 0) setOrdersLoading(true);
        const list = await factoryService.listFactoryOrders();
        if (!cancelled) setOrders(list);
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  useEffect(() => {
    if (ordersLoading || !activeOrderId) return;
    if (!orders.some((o) => o.id === activeOrderId)) {
      setActiveOrderId(null);
    }
  }, [activeOrderId, orders, ordersLoading, setActiveOrderId]);

  const activeOrder = useMemo(
    () => orders.find((o) => o.id === activeOrderId) || null,
    [activeOrderId, orders],
  );

  const value = useMemo<FactoryOrderScopeValue>(
    () => ({
      activeOrderId,
      activeOrder,
      setActiveOrderId,
      orders,
      ordersLoading,
    }),
    [activeOrder, activeOrderId, orders, ordersLoading, setActiveOrderId],
  );

  return (
    <FactoryOrderScopeContext.Provider value={value}>
      {children}
    </FactoryOrderScopeContext.Provider>
  );
}

export function useFactoryOrderScope() {
  const value = useContext(FactoryOrderScopeContext);
  if (!value) throw new Error('useFactoryOrderScope must be used inside FactoryOrderScopeProvider');
  return value;
}
